import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../config/apiConfig';
import Swal from 'sweetalert2';

const PlanPaymentModal = ({ isOpen, onClose, companyId, planId, planName, finalPrice, planPrice, planDiscount, defaultCouponCode, defaultCouponDiscount, onSuccess, isRenewal, userId }) => {
    // Determine initial plan ID from props if available (might need to map planName to ID later, but we'll default to first or currently selected)
    const [selectedPlanId, setSelectedPlanId] = useState(planId || '');
    const [currentPlanName, setCurrentPlanName] = useState(planName || '');
    const [amountPaid, setAmountPaid] = useState(finalPrice || '');
    const [paymentMode, setPaymentMode] = useState('Online');
    const [transactionId, setTransactionId] = useState('');
    const [paymentNote, setPaymentNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [plans, setPlans] = useState([]);
    const [couponCode, setCouponCode] = useState(defaultCouponCode || '');
    const [appliedCoupon, setAppliedCoupon] = useState(defaultCouponCode ? { isDefault: true, discount_value: defaultCouponDiscount } : null);
    const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

    useEffect(() => {
        if (isRenewal && isOpen) {
            fetch(`${API_BASE_URL}/plan/public/all`)
                .then(res => res.json())
                .then(data => {
                    const fetchedPlans = Array.isArray(data.plans) ? data.plans : (Array.isArray(data) ? data : []);
                    setPlans(fetchedPlans);
                    if (fetchedPlans.length > 0) {
                        // Match the current planName to get its ID, or just select the first one
                        const existingPlan = fetchedPlans.find(p => p.plan_name === planName || p.planName === planName);
                        if (existingPlan) {
                            setSelectedPlanId(existingPlan.id);
                            setAmountPaid(existingPlan.final_price || existingPlan.finalPrice || 0);
                        } else {
                            setSelectedPlanId(fetchedPlans[0].id);
                            setCurrentPlanName(fetchedPlans[0].plan_name || fetchedPlans[0].planName);
                            setAmountPaid(fetchedPlans[0].final_price || fetchedPlans[0].finalPrice || 0);
                        }
                    }
                })
                .catch(err => console.error("Error fetching plans:", err));
        }
    }, [isRenewal, isOpen, planName]);

    useEffect(() => {
        if (isOpen && !isRenewal) {
            setCouponCode(defaultCouponCode || '');
            setAmountPaid(finalPrice || '');
            setSelectedPlanId(planId || '');
            setAppliedCoupon(defaultCouponCode ? { isDefault: true, discount_value: defaultCouponDiscount } : null);
        }
    }, [isOpen, isRenewal, defaultCouponCode, finalPrice, planId, defaultCouponDiscount]);

    const handlePlanChange = (e) => {
        const pId = e.target.value;
        setSelectedPlanId(pId);
        setAppliedCoupon(null); // Reset coupon when plan changes
        const plan = plans.find(p => String(p.id) === String(pId));
        if (plan) {
            setCurrentPlanName(plan.plan_name || plan.planName);
            setAmountPaid(plan.final_price || plan.finalPrice || 0);
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode) {
            toast.error("Please enter a coupon code");
            return;
        }
        if (!selectedPlanId && isRenewal) {
            toast.error("Please select a plan first");
            return;
        }
        try {
            setIsValidatingCoupon(true);
            const response = await fetch(`${API_BASE_URL}/coupon/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    couponCode,
                    planId: selectedPlanId,
                    companyId: companyId
                })
            });

            const data = await response.json();

            if (data.valid) {
                setAppliedCoupon(data.coupon);
                toast.success("Coupon applied successfully!");
                
                // Calculate new amount
                let base = 0;
                if (isRenewal) {
                    const plan = plans.find(p => String(p.id) === String(selectedPlanId));
                    if (plan) base = Number(plan.final_price || plan.finalPrice || 0);
                } else {
                    base = Number(finalPrice || 0);
                }

                if (base > 0 || base === 0) {
                    let discount = 0;
                    if (data.coupon.discount_type === 'percentage' || data.coupon.discountType === 'percentage') {
                        discount = base * (Number(data.coupon.discount_value || data.coupon.discountValue) / 100);
                    } else {
                        discount = Number(data.coupon.discount_value || data.coupon.discountValue);
                    }
                    setAmountPaid(Math.max(0, base - discount).toFixed(2));
                }
            } else {
                toast.error(data.message || "Invalid coupon");
            }
        } catch (err) {
            console.error("Coupon validation error:", err);
            toast.error("Failed to validate coupon");
        } finally {
            setIsValidatingCoupon(false);
        }
    };

    if (!isOpen) return null;



    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;

        e.preventDefault();
        
        if (!amountPaid || isNaN(amountPaid) || Number(amountPaid) <= 0) {
            toast.error('Please enter a valid amount.');
            return;
        }

        try {
            setIsSubmitting(true);
            const token = sessionStorage.getItem('token');
            const payload = {
                company_id: companyId,
                amount_paid: amountPaid,
                payment_mode: paymentMode
            };
            if (isRenewal && userId) payload.user_id = userId;
            if (selectedPlanId) payload.plan_id = selectedPlanId;
            if (transactionId) payload.transaction_id = transactionId;
            if (paymentNote) payload.payment_note = paymentNote;
            if (appliedCoupon) payload.coupon_code = appliedCoupon.coupon_code || appliedCoupon.couponCode;

            const endpoint = isRenewal ? `${API_BASE_URL}/plan-payment/renew` : `${API_BASE_URL}/plan-payment`;
            const headers = {
                'Content-Type': 'application/json'
            };
            if (!isRenewal && token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('Payment submitted successfully. Awaiting verification.');
                if (onSuccess) onSuccess();
                onClose();
            } else {
                const errData = await response.json();
                toast.error(errData.message || 'Failed to submit payment.');
            }
        } catch (error) {
            console.error('Submit payment error:', error);
            toast.error('An error occurred while submitting the payment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
            <div className="modal-content" style={{
                background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '500px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', overflow: 'hidden'
            }}>
                <div className="modal-header" style={{
                    padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    backgroundColor: '#f8fafc'
                }}>
                    <h5 style={{ margin: 0, color: '#1e293b', fontWeight: '600' }}>Submit Plan Payment</h5>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b'
                    }}>&times;</button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="modal-body" style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                        
                        {isRenewal ? (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569', fontWeight: '600' }}>Select Plan <span style={{color: 'red'}}>*</span></label>
                                <select
                                    value={selectedPlanId}
                                    onChange={handlePlanChange}
                                    required
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', fontSize: '14px', color: '#1e293b' }}
                                >
                                    <option value="">-- Choose Plan --</option>
                                    {plans.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.plan_name || p.planName} (₹{p.final_price || p.finalPrice})
                                        </option>
                                    ))}
                                </select>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', padding: '8px 12px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                    <span style={{ color: '#166534', fontSize: '14px', fontWeight: '500' }}>Selected Plan Payable Amount:</span>
                                    <span style={{ fontWeight: '700', color: '#166534' }}>₹{amountPaid || 0}</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span style={{ color: '#475569', fontSize: '14px' }}>Plan:</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>{planName || 'N/A'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span style={{ color: '#475569', fontSize: '14px' }}>Total Amount:</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>₹{planPrice || 0}</span>
                                </div>
                                {(planDiscount > 0) && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{ color: '#475569', fontSize: '14px' }}>Plan Discount ({planDiscount}%):</span>
                                        <span style={{ fontWeight: '600', color: '#16a34a' }}>- ₹{((planPrice * planDiscount) / 100).toFixed(2)}</span>
                                    </div>
                                )}
                                {(defaultCouponCode || appliedCoupon) && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{ color: '#475569', fontSize: '14px' }}>Coupon Discount:</span>
                                        <span style={{ fontWeight: '600', color: '#16a34a' }}>
                                            - ₹{defaultCouponCode ? (defaultCouponDiscount || 0) : (planPrice - ((planPrice * planDiscount) / 100) - amountPaid).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #bfdbfe', paddingTop: '4px', marginTop: '4px' }}>
                                    <span style={{ color: '#475569', fontSize: '14px', fontWeight: '600' }}>Total Payable Amount:</span>
                                    <span style={{ fontWeight: '700', color: '#0f172a' }}>₹{amountPaid || 0}</span>
                                </div>
                            </div>
                        )}

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569' }}>Apply Coupon Code</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="COUPON CODE"
                                    readOnly={!!defaultCouponCode}
                                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', textTransform: 'uppercase', backgroundColor: defaultCouponCode ? '#f1f5f9' : '#fff' }}
                                />
                                {!defaultCouponCode && (
                                    <button 
                                        type="button"
                                        onClick={handleApplyCoupon}
                                        disabled={isValidatingCoupon || !couponCode}
                                        style={{ 
                                            padding: '8px 16px', 
                                            backgroundColor: '#0c3447', 
                                            color: '#fff', 
                                            border: 'none', 
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            opacity: (isValidatingCoupon || !couponCode) ? 0.7 : 1
                                        }}
                                    >
                                        {isValidatingCoupon ? '...' : 'Apply'}
                                    </button>
                                )}
                            </div>
                            {appliedCoupon && !defaultCouponCode && (
                                <small style={{ color: '#16a34a', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
                                    ✓ Coupon Applied: {appliedCoupon.discount_type === 'percentage' || appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discount_value || appliedCoupon.discountValue}% Off` : `₹${appliedCoupon.discount_value || appliedCoupon.discountValue} Off`}
                                </small>
                            )}
                            {defaultCouponCode && (
                                <small style={{ color: '#16a34a', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
                                    ✓ Coupon {defaultCouponCode} Applied Automatically
                                </small>
                            )}
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569' }}>Amount Paid <span style={{color: 'red'}}>*</span></label>
                            <input 
                                type="number" 
                                className="form-control" 
                                value={amountPaid}
                                readOnly
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569' }}>Payment Mode <span style={{color: 'red'}}>*</span></label>
                            <select 
                                className="form-select" 
                                value={paymentMode}
                                onChange={(e) => setPaymentMode(e.target.value)}
                                required
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                            >
                                <option value="Online">Online</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="UPI">UPI</option>
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569' }}>Transaction ID / Reference No.</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                placeholder="E.g. UTR number, UPI Ref"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                            />
                        </div>



                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#475569' }}>Notes (Optional)</label>
                            <textarea 
                                className="form-control" 
                                value={paymentNote}
                                onChange={(e) => setPaymentNote(e.target.value)}
                                rows="2"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', resize: 'vertical' }}
                            ></textarea>
                        </div>
                    </div>
                    
                    <div className="modal-footer" style={{
                        padding: '16px 20px', borderTop: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'flex-end', gap: '10px',
                        backgroundColor: '#f8fafc'
                    }}>
                        <button type="button" onClick={onClose} style={{
                            padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #cbd5e1',
                            color: '#475569', borderRadius: '4px', cursor: 'pointer', fontWeight: '500'
                        }} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" style={{
                            padding: '8px 16px', backgroundColor: '#0ea5e9', border: 'none',
                            color: '#fff', borderRadius: '4px', cursor: 'pointer', fontWeight: '500'
                        }} disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit Payment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PlanPaymentModal;
